"use client"

import { useState, useEffect } from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { X, TrendingUp, Clock, Star, MapPin, Leaf, Eye } from "lucide-react"
import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { communityAnalytics } from "@/lib/analytics"

interface FilterBarProps {
  initialFilters?: {
    sortBy?: 'newest' | 'top-rated' | 'most-viewed' | 'trending' | 'controversial'
    grassType?: string
    region?: string
  }
  onFiltersChange?: (filters: {
    sortBy: 'newest' | 'top-rated' | 'most-viewed' | 'trending' | 'controversial'
    grassType?: string
    region?: string
  }) => void
}

export function FilterBar({ initialFilters, onFiltersChange }: FilterBarProps) {
  const [sortBy, setSortBy] = useState<'newest' | 'top-rated' | 'most-viewed' | 'trending' | 'controversial'>(initialFilters?.sortBy || 'newest')
  const [grassType, setGrassType] = useState<string>(initialFilters?.grassType || 'all')
  const [region, setRegion] = useState<string>(initialFilters?.region || 'all')

  const GRASS_TYPES = Object.keys(GRASS_CHARACTERISTICS)

  // Major US regions (consolidated from ZIP code ranges)
  const REGIONS = [
    { value: 'northeast', label: 'Northeast' },
    { value: 'southeast', label: 'Southeast' },
    { value: 'midwest', label: 'Midwest' },
    { value: 'southwest', label: 'Southwest' },
    { value: 'west', label: 'West Coast' },
  ]

  useEffect(() => {
    setSortBy(initialFilters?.sortBy || 'newest')
    setGrassType(initialFilters?.grassType || 'all')
    setRegion(initialFilters?.region || 'all')
  }, [initialFilters?.grassType, initialFilters?.region, initialFilters?.sortBy])

  useEffect(() => {
    onFiltersChange?.({
      sortBy,
      grassType: grassType === 'all' ? undefined : grassType,
      region: region === 'all' ? undefined : region
    })
  }, [sortBy, grassType, region, onFiltersChange])

  const clearFilters = () => {
    setGrassType('all')
    setRegion('all')
    setSortBy('newest')
    communityAnalytics.filtersChanged({ sortBy: 'newest' })
  }

  const hasActiveFilters = grassType !== 'all' || region !== 'all' || sortBy !== 'newest'

  const SORT_OPTIONS = [
    { value: 'newest' as const, label: 'New', icon: Clock },
    { value: 'top-rated' as const, label: 'Top', icon: Star },
    { value: 'most-viewed' as const, label: 'Viewed', icon: Eye },
    { value: 'trending' as const, label: 'Trending', icon: TrendingUp },
  ]

  return (
    <div className="ds-glass rounded-3xl p-3 md:p-4">
      {/* Single row: Sort buttons + Filter dropdowns */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Sort Options - horizontal scroll on mobile, green theme */}
        <div className="flex items-center gap-1.5 overflow-x-auto scroll-hint-fade pb-1 sm:pb-0 -mx-1 px-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-green-900/70 flex-shrink-0">Sort:</span>
          {SORT_OPTIONS.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                setSortBy(value)
                communityAnalytics.filtersChanged({
                  sortBy: value,
                  grassType: grassType === 'all' ? undefined : grassType,
                  region: region === 'all' ? undefined : region,
                })
              }}
              aria-pressed={sortBy === value}
              className={`flex min-h-10 flex-shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-semibold transition-colors ${
                sortBy === value
                  ? 'bg-green-950 text-white shadow-sm'
                  : 'bg-green-50 text-green-900 hover:bg-green-100 hover:text-green-700'
              }`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>

        {/* Filter dropdowns */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
          <Select value={grassType} onValueChange={(value) => {
            setGrassType(value)
            communityAnalytics.filtersChanged({
              sortBy,
              grassType: value === 'all' ? undefined : value,
              region: region === 'all' ? undefined : region,
            })
          }}>
            <SelectTrigger className="w-auto min-w-[100px] rounded-full border-white/60 bg-white/70 text-green-950 shadow-xs">
              <Leaf className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
              <span>{grassType === 'all' ? 'All grass' : grassType}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All grass types</SelectItem>
              {GRASS_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={region} onValueChange={(value) => {
            setRegion(value)
            communityAnalytics.filtersChanged({
              sortBy,
              grassType: grassType === 'all' ? undefined : grassType,
              region: value === 'all' ? undefined : value,
            })
          }}>
            <SelectTrigger className="w-auto min-w-[100px] rounded-full border-white/60 bg-white/70 text-green-950 shadow-xs">
              <MapPin className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
              <span>{region === 'all' ? 'All regions' : REGIONS.find(r => r.value === region)?.label}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All regions</SelectItem>
              {REGIONS.map((regionOption) => (
                <SelectItem key={regionOption.value} value={regionOption.value}>
                  {regionOption.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="h-11 md:h-10 w-11 md:w-10 flex items-center justify-center rounded-full bg-white/70 text-green-900 shadow-xs hover:text-green-600 hover:bg-white transition-colors"
              title="Clear filters"
              aria-label="Clear filters"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
